import { useState, useEffect, useRef, useCallback } from 'react';
import { X, FileText, User, Building2, CheckCircle, XCircle, Clock, Truck, MapPin, Package } from 'lucide-react';
import API_BASE_URL from "../config";
import bellSound from '../SoundBell/Soundbells.mp3';
import useNotificationPolling from '../utils/useNotificationPolling';


// Delivery due-date alerts: only POs the signed-in user created, delivered
// within this many days (today included). See Notification.jsx for the bell side.
const DUE_SOON_DAYS = 2;
const DUE_MAX_TOASTS = 3;                 // avoid a wall of cards if many are due
const PO_STATUS_APPROVED = 2;             // 0 Draft · 1 Pending · 2 Approved · 3 Rejected
const RECEIVING_CLOSED = ['complete', 'completed', 'cancelled', 'canceled', 'closed'];

const STATUS_MAP = {
  '-1': { label: 'Rejected', bg: 'from-red-500 to-red-600',         icon: XCircle,     body: 'Your purchase order has been rejected.' },
  '0':  { label: 'Draft',    bg: 'from-gray-500 to-gray-600',       icon: FileText,    body: 'Your purchase order is in draft.' },
  '1':  { label: 'Pending',  bg: 'from-amber-500 to-amber-600',     icon: Clock,       body: 'Your purchase order is pending approval.' },
  '2':  { label: 'Approved', bg: 'from-emerald-500 to-emerald-600', icon: CheckCircle, body: 'Your purchase order has been approved!' },
  '3':  { label: 'Issued',   bg: 'from-blue-500 to-blue-700',       icon: Truck,       body: 'Your purchase order has been issued!' },
  '4':  { label: 'Received', bg: 'from-teal-500 to-teal-700',       icon: CheckCircle, body: 'Your purchase order has been received!' },
};

export default function PONotification() {
  const [alerts, setAlerts]               = useState([]);
  const [profileCache, setProfileCache]   = useState({});
  const [vendorCache, setVendorCache]     = useState({});
  const [userNameCache, setUserNameCache] = useState({});
  const [notifSettings, setNotifSettings] = useState({ notice_sound: true, notice_view: true });

  const prevDataRef      = useRef(null);
  const lastUpdatedRef   = useRef(null);
  const userInteracted   = useRef(false);
  const dueAlertedRef    = useRef(new Set());   // `${poId}:${deliveryDate}` already announced this session

  const currentUser   = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?.userId;

  // ── Fetch NotificationSettings once on mount ──
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${API_BASE_URL}api/NotificationSettings/user/${currentUserId}`)
      .then(r => r.json())
      .then(d => setNotifSettings(d))
      .catch(() => {});
  }, [currentUserId]);

  const isSoundOn = (s) => s === true || s === 1 || String(s).toLowerCase() === 'true';
  const isViewOn  = (s) => s === true || s === 1 || String(s).toLowerCase() === 'true';

  useEffect(() => {
    const handler = () => { userInteracted.current = true; };
    window.addEventListener('click',   handler);
    window.addEventListener('keydown', handler);
    window.addEventListener('scroll',  handler);
    return () => {
      window.removeEventListener('click',   handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('scroll',  handler);
    };
  }, []);

  const playSound = useCallback(() => {
    // ── Blocked if notice_sound is false ──
    if (!isSoundOn(notifSettings.notice_sound)) return;
    try {
      const audio = new Audio(bellSound);
      audio.volume = 0.7;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          const retry = () => { audio.play().catch(() => {}); window.removeEventListener('click', retry); };
          window.addEventListener('click', retry, { once: true });
        });
      }
    } catch (err) {}
  }, [notifSettings.notice_sound]);

  const fetchUserName = useCallback(async (userId) => {
    if (!userId) return null;
    if (userNameCache[userId] !== undefined) return userNameCache[userId];
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}`);
      if (!res.ok) return null;
      const data = await res.json();
      const name = data?.fullName || data?.FullName || data?.userName || data?.UserName ||
        (data?.firstName ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : null) ||
        data?.name || data?.Name || null;
      setUserNameCache(prev => ({ ...prev, [userId]: name }));
      return name;
    } catch {
      setUserNameCache(prev => ({ ...prev, [userId]: null }));
      return null;
    }
  }, [userNameCache]);

  const fetchVendor = useCallback(async (vendorId) => {
    if (!vendorId || vendorCache[vendorId] !== undefined) return vendorCache[vendorId] || null;
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      if (!res.ok) return null;
      const data = await res.json();
      const vendors = Array.isArray(data) ? data : [];
      const map = {};
      vendors.forEach(v => { map[v.id] = v.VendorName || v.vendorName || 'Unknown Vendor'; });
      setVendorCache(map);
      return map[vendorId] || null;
    } catch { return null; }
  }, [vendorCache]);

  const fetchPODetails = useCallback(async (poId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/T_Po_Details`);
      if (!res.ok) return [];
      const data = await res.json();
      const all  = Array.isArray(data) ? data : [];
      return all.filter(d => d.poHeaderId === poId || d.pOHeaderId === poId);
    } catch { return []; }
  }, []);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return;
    setProfileCache(prev => {
      if (prev[userId] !== undefined) return prev;
      return { ...prev, [userId]: null };
    });
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}/profile`);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          const pic  = data?.profileBase64 || null;
          setProfileCache(prev => ({ ...prev, [userId]: pic ? `data:image/jpeg;base64,${pic}` : null }));
        } else {
          const blob = await res.blob();
          const url  = URL.createObjectURL(blob);
          setProfileCache(prev => ({ ...prev, [userId]: url }));
        }
      }
    } catch {
      setProfileCache(prev => ({ ...prev, [userId]: null }));
    }
  }, []);

  const addAlert = useCallback((alert) => {
    // ── Blocked if notice_view is false ──
    if (!isViewOn(notifSettings.notice_view)) {
      playSound();
      return;
    }
    const alertWithId = { ...alert, uid: `${alert.poId}-${alert.type}-${Date.now()}` };
    setAlerts(prev => [...prev, alertWithId]);
    if (alert.userId) fetchProfile(alert.userId);
    playSound();
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.uid !== alertWithId.uid));
    }, 8000);
  }, [fetchProfile, playSound, notifSettings.notice_view]);

  const processData = useCallback(async (arr, isFirstLoad) => {
    if (isFirstLoad) {
      const snapshot = {};
      arr.forEach(po => {
        snapshot[po.id] = { status: po.status, poCreatedDate: po.poCreatedDate };
      });
      prevDataRef.current    = snapshot;
      const dates            = arr.map(p => new Date(p.poCreatedDate || 0));
      lastUpdatedRef.current = dates.length ? Math.max(...dates) : 0;
      return;
    }

    const prev        = prevDataRef.current || {};
    const newSnapshot = {};
    let latestDate    = lastUpdatedRef.current || 0;

    for (const po of arr) {
      const creDate = new Date(po.poCreatedDate || 0).getTime();
      if (creDate > latestDate) latestDate = creDate;

      if (!prev[po.id]) {
        if (String(po.preparedBy) !== String(currentUserId)) {
          const [vendorName, details, preparedByName] = await Promise.all([
            fetchVendor(po.vendorId),
            fetchPODetails(po.id),
            po.preparedByName ? Promise.resolve(po.preparedByName) : fetchUserName(po.preparedBy),
          ]);
          const itemSummary = details.length > 0
            ? details.slice(0, 2).map(d => d.itemDescription || d.description || d.itemCode || 'Item').join(', ')
              + (details.length > 2 ? ` +${details.length - 2} more` : '')
            : null;
          addAlert({
            type: 'new', poId: po.id, userId: po.preparedBy,
            title: 'New Purchase Order',
            name: preparedByName || 'Unknown', requestor: preparedByName || 'Unknown',
            preparedById: po.preparedBy, controlNo: po.poNumber || 'N/A',
            vendorName: vendorName || 'Unknown Vendor',
            shipTo: po.shipTo_Location || po.shipTo_Address1 || 'N/A',
            itemSummary, itemCount: details.length,
            gradient: 'from-blue-600 to-blue-700', Icon: Truck,
            message: 'created a new purchase order.',
          });
        }
      }

      if (prev[po.id] && prev[po.id].status !== po.status) {
        if (String(po.preparedBy) === String(currentUserId)) {
          const s = STATUS_MAP[String(po.status)];
          if (s) {
            const [vendorName, details, preparedByName] = await Promise.all([
              fetchVendor(po.vendorId),
              fetchPODetails(po.id),
              po.preparedByName ? Promise.resolve(po.preparedByName) : fetchUserName(po.preparedBy),
            ]);
            const itemSummary = details.length > 0
              ? details.slice(0, 2).map(d => d.itemDescription || d.description || d.itemCode || 'Item').join(', ')
                + (details.length > 2 ? ` +${details.length - 2} more` : '')
              : null;
            addAlert({
              type: 'status', poId: po.id, userId: po.preparedBy,
              title: `PO ${s.label}`,
              name: preparedByName || 'Your PO', requestor: preparedByName || 'Unknown',
              preparedById: po.preparedBy, controlNo: po.poNumber || 'N/A',
              vendorName: vendorName || 'Unknown Vendor',
              shipTo: po.shipTo_Location || po.shipTo_Address1 || 'N/A',
              itemSummary, itemCount: details.length,
              gradient: s.bg, Icon: s.icon, message: s.body,
            });
          }
        }
      }

      newSnapshot[po.id] = { status: po.status, poCreatedDate: po.poCreatedDate };
    }

    prevDataRef.current    = newSnapshot;
    lastUpdatedRef.current = latestDate;
  }, [currentUserId, addAlert, fetchVendor, fetchPODetails, fetchUserName]);

  // ── Delivery due within DUE_SOON_DAYS → toast, but only for the PO's creator ──
  const processDueDates = useCallback(async (arr) => {
    if (!currentUserId) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueNow = [];
    for (const po of arr) {
      if (String(po.preparedBy) !== String(currentUserId)) continue;   // 👈 who created the PO
      if (Number(po.status) !== PO_STATUS_APPROVED) continue;
      if (RECEIVING_CLOSED.includes(String(po.receivingStatus || '').trim().toLowerCase())) continue;
      if (!po.deliveryDate) continue;

      const due = new Date(po.deliveryDate);
      if (isNaN(due)) continue;
      due.setHours(0, 0, 0, 0);

      const days = Math.round((due - today) / 86400000);
      if (days < 0 || days > DUE_SOON_DAYS) continue;

      const key = `${po.id}:${String(po.deliveryDate).slice(0, 10)}`;
      if (dueAlertedRef.current.has(key)) continue;
      dueAlertedRef.current.add(key);
      dueNow.push({ po, days });
    }

    dueNow.sort((a, b) => a.days - b.days);
    for (const { po, days } of dueNow.slice(0, DUE_MAX_TOASTS)) {
      const when = days === 0
        ? 'is due for delivery today'
        : days === 1
          ? 'is due for delivery tomorrow'
          : `is due for delivery in ${days} days`;
      await addAlert({
        type: 'due', poId: po.id, userId: po.preparedBy,
        title: 'Delivery Due',
        name: po.vendorName || 'Vendor', requestor: po.vendorName || 'Vendor',
        preparedById: po.preparedBy, controlNo: po.poNumber || 'N/A',
        vendorName: po.vendorName || 'Unknown Vendor',
        shipTo: po.shipTo_Location || po.shipTo_Address1 || 'N/A',
        itemSummary: null, itemCount: 0,
        gradient: 'from-amber-500 to-orange-600', Icon: Clock,
        message: `${when}.`,
      });
    }
  }, [currentUserId, addAlert]);

  useNotificationPolling(`${API_BASE_URL}api/T_PO_Header`, currentUserId, async (arr) => {
    await processData(arr, prevDataRef.current === null);
    await processDueDates(arr);
  });

  const dismiss = (uid) => setAlerts(prev => prev.filter(a => a.uid !== uid));

  // ── If notice_view is off or no alerts, render nothing ──
  if (!isViewOn(notifSettings.notice_view) || alerts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9997] flex flex-col gap-3 items-end pointer-events-none">
      {alerts.map((alert, idx) => {
        const Icon       = alert.Icon;
        const profilePic = profileCache[alert.userId];
        return (
          <div
            key={alert.uid}
            className="w-[360px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto"
            style={{ animation: 'slideUp 0.3s ease-out both', animationDelay: `${idx * 60}ms` }}
          >
            <div className={`bg-gradient-to-r ${alert.gradient} px-4 py-2.5 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-white text-xs font-bold uppercase tracking-wide">{alert.title}</span>
              </div>
              <button onClick={() => dismiss(alert.uid)} className="p-1 bg-white/10 hover:bg-white/25 rounded-lg transition-all">
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>

            <div className="px-4 py-3.5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {profilePic ? (
                    <img src={profilePic} alt="profile" className="w-10 h-10 rounded-xl object-cover border border-gray-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <User className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                    <span className="font-bold text-gray-900 text-sm truncate">{alert.requestor}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{alert.message}</p>
                  <div className="flex items-center gap-1 mb-1.5">
                    <FileText className="w-3 h-3 text-blue-400 flex-shrink-0" />
                    <span className="font-mono font-bold text-blue-700 text-xs">{alert.controlNo}</span>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 space-y-1.5">
                    <div className="flex items-start gap-1.5">
                      <Building2 className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-blue-400 font-bold uppercase block">Vendor</span>
                        <span className="text-xs font-bold text-gray-800 truncate block">{alert.vendorName}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <MapPin className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-blue-400 font-bold uppercase block">Ship To</span>
                        <span className="text-xs text-gray-700 truncate block">{alert.shipTo}</span>
                      </div>
                    </div>
                    {alert.itemSummary && (
                      <div className="flex items-start gap-1.5">
                        <Package className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-[10px] text-blue-400 font-bold uppercase block">Items ({alert.itemCount})</span>
                          <span className="text-xs text-gray-700 block leading-tight">{alert.itemSummary}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="h-1 bg-gray-100">
              <div className={`h-full bg-gradient-to-r ${alert.gradient} rounded-full`} style={{ animation: 'shrink 8s linear forwards' }} />
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes shrink  { from { width: 100%; } to { width: 0%; } }
      `}</style>
    </div>
  );
}
